import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';
import { leadsService } from '@/api/leads.service';
import type { Lead, UpdateLeadDto } from '@carvia/ros-client-types';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';

export const LEADS_QUERY_KEY = 'leads';

export const useLead = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [LEADS_QUERY_KEY, toRef(id)],
    queryFn: () => leadsService.getOne(toValue(id)),
    enabled: () => !!toValue(id) && toValue(enabled),
  });
};

export const useLeadsByEmail = (
  email: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [LEADS_QUERY_KEY, 'by-email', toRef(email)],
    queryFn: () =>
      leadsService.getAllWithQuery({
        email: toValue(email),
        limit: 20,
      }),
    enabled: () => !!toValue(email) && toValue(enabled),
  });
};

type UpdateLeadVars = { id: string; data: UpdateLeadDto; successMessage?: string };

export const useUpdateLead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [LEADS_QUERY_KEY],
    mutationFn: ({ id, data }: UpdateLeadVars) => leadsService.update(id, data),
    onMutate: ({ successMessage }: UpdateLeadVars): MutationDefaultsContext => ({
      successMessage: successMessage ?? 'Lead updated',
    }),
    onSuccess: (result, vars, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, vars, onMutateResult, context);
      queryClient.setQueryData<Lead>([LEADS_QUERY_KEY, result.id], result);
      queryClient.invalidateQueries({ queryKey: [LEADS_QUERY_KEY] });
    },
  });
};
